import axios from "axios";
import AuthService from "../services/authentication.service";
import Toast from "../components/Toast";
import { STORAGE_KEY as LANGUAGE_STORAGE_KEY } from "../../i18n/LanguageContext";
import { DEFAULT_LANGUAGE_CODE } from "../../i18n/languages";
import { rememberRedirectAfterLogin } from "../../pages/auth/redirectAfterLogin";

const { REACT_APP_BASE_URL, REACT_APP_FILE_MANAGEMENT } = process.env;
// const token = AuthService.getAccessToken();

const https = axios.create({
   baseURL: REACT_APP_BASE_URL,
   timeout: 120000,
   headers: { "Content-Type": "application/json"}
   // headers: { 
   //    "Content-Type": "application/json",
   //    ...(token && token !== "" && { "Authorization": `Bearer ${token}` }) 
   // },
});

let isRefreshing = false;
let refreshSubscribers = [];
let interceptorsInstalled = false;
// Replaced on every setupInterceptor call so the latest loader setter is used,
// while the interceptors themselves are only registered once.
let setGlobalLoader = () => {};

const sendToLogin = () => {
   AuthService.logout();
   // Come back to this page after logging in again.
   rememberRedirectAfterLogin(window.location);
   window.location.href = "/auth/login";
};

// The refresh call bypasses the `https` interceptors: a 401 on it must not
// queue behind itself waiting for a refresh that will never finish.
const refreshAccessToken = async () => {
   const { data } = await axios.post(
      `${REACT_APP_BASE_URL}/Accounts/refresh-token`,
      { refreshToken: AuthService.getRefreshToken() },
      { headers: { "Content-Type": "application/json" } }
   );
   if (!data?.data?.token) throw new Error("Refresh token rejected");
   AuthService.setTokens(data.data.token, data.data.refreshToken);
};

// Must be called before the first request is sent (see InterceptorProvider):
// without it requests go out with no Authorization header.
export const setupInterceptor = (store, setLoader) => {
   if (setLoader) setGlobalLoader = setLoader;
   if (interceptorsInstalled) return;
   interceptorsInstalled = true;

   https.interceptors.request.use((config) => {
      const token = AuthService.getAccessToken();

      config.headers.Authorization = `Bearer ${token}`;

      // Read on every request (not cached) so a language switch takes effect
      // immediately, the same way the Authorization token above is re-read.
      config.headers["X-Language"] =
         localStorage.getItem(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE_CODE;

      if (config.url && config.url.startsWith("/FileData/getFileUrlByFileKeyAndEntityId")) {
         config.baseURL = REACT_APP_FILE_MANAGEMENT;
      } else {
         config.baseURL = REACT_APP_BASE_URL;
      }

      return config;
   });

   https.interceptors.response.use(
      (response) => {
         if (!response.data.success) {
            const errMessages = response?.data?.messages?.join("");
            Toast.warning(errMessages);
         }
         return response;
      },
      (error) => {
         if (!navigator.onLine) {
            Toast.warning("Check your internet connection");
            return Promise.reject(error);
         }
         return handleException(error);
      }
   );

   // On a 401 the access token has expired: refresh it once, then replay the failed
   // request (and any that failed while the refresh was running) with the new token.
   const handleException = async (error) => {
      const originalRequest = error.config;

      if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
         return Promise.reject(error);
      }

      if (!AuthService.getRefreshToken()) {
         sendToLogin();
         return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
         return new Promise((resolve, reject) => {
            refreshSubscribers.push((refreshed) =>
               refreshed ? resolve(https(originalRequest)) : reject(error)
            );
         });
      }

      isRefreshing = true;
      setGlobalLoader(true);

      try {
         await refreshAccessToken();
         refreshSubscribers.forEach((callback) => callback(true));
         return https(originalRequest);
      } catch (refreshError) {
         refreshSubscribers.forEach((callback) => callback(false));
         sendToLogin();
         return Promise.reject(refreshError);
      } finally {
         refreshSubscribers = [];
         isRefreshing = false;
         setGlobalLoader(false);
      }
   };
};

export default https;
